import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import logger from '../utils/logger';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import { 
  Box, 
  Dialog, 
  IconButton, 
  Typography, 
  Container, 
  Paper, 
  ToggleButton, 
  ToggleButtonGroup 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';



function ShareObjectPage() {
  const { uuid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' или 'layout'

  useEffect(() => {

    logger.log('🎢 ShareObjectPage useEffect')
    logger.log('import.meta.env.VITE_API_URL', import.meta.env.VITE_API_URL)
    const fetchObject = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${import.meta.env.VITE_API_URL}/objects/${uuid}`);
        if (!response.ok) {
          throw new Error('Не удалось загрузить объект');
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
        logger.log('Ошибка при загрузке объекта:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchObject();
  }, [uuid]);

  if (loading) return <div className="flex justify-center items-center h-screen">Загрузка...</div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  if (!data) return <div className="flex justify-center items-center h-screen">Объект не найден</div>;

  const { object, model } = data;

  // Используем напрямую imageUrl из объектов фотографий
  const photos = (object.photos || []).map(photo => photo.imageUrl).filter(Boolean);
  const layoutPhotos = (object.layoutPhotos || []).map(photo => photo.imageUrl).filter(Boolean);
  
  // Выбираем отображаемые изображения в зависимости от активной вкладки
  const images = activeTab === 'photos' ? photos : layoutPhotos;

  // Обработчик изменения вкладки
  const handleTabChange = (event, newTab) => {
    if (newTab !== null) setActiveTab(newTab);
  };

  // Функция для отображения значения поля с учетом модели данных
  const renderFieldValue = (field, value) => {
    if (!field) return null;

    const { controlType, options, multiple } = field;

    if (controlType === 'buttons' || controlType === 'dropdown') {
      // Если значение - массив (множественный выбор)
      if (Array.isArray(value)) {
        const selectedOptions = value
          .map((id) => options.find((option) => option.id === id)?.value)
          .filter(Boolean);
        return selectedOptions.join(', ');
      }
      // Если значение - одиночный выбор
      const selectedOption = options.find((option) => option.id === value);
      return selectedOption?.value || 'Неизвестно';
    }

    // Для других типов полей просто вернуть значение
    return value;
  };

  // Поля, которые не нужно отображать в основном потоке
  const excludedFields = ['description', 'city', 'address', 'price'];

  // Функция для рендера полей объекта
  const renderFields = () => {
    if (!model) {
      return <p>Нет доступных данных для отображения.</p>;
    }

    const fields = [];

    // Проходим по всем полям модели
    Object.entries(model).forEach(([key, field]) => {
      // Пропускаем исключенные поля и технические поля
      if (key === 'type' || excludedFields.includes(key) || key === 'rooms') return;

      // Проверяем, есть ли поле в объекте или в details
      let value = object[key];
      if (value === undefined || value === null) {
        value = object.details?.[key];
      }

      if (value === undefined || value === null) return;

      // Стандартный рендер для полей в виде двух колонок с разделителем
      fields.push(
        <Box key={key} sx={{ marginBottom: 0 }}>
          <div className="flex flex-row items-center justify-between py-4 border-b border-gray-400">
            <Typography sx={{ color: 'text.secondary' }} className="font-medium text-base">
              {field.label}
            </Typography>
            <div className="text-right">{renderFieldValue(field, value)}</div>
          </div>
        </Box>
      );
    });

    return fields;
  };

  const handleImageClick = (image) => {
    setFullscreenImage(image);
    setFullscreenOpen(true);
  };

  return (
    <Container maxWidth="lg" className="py-8">
      
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && <div onClick={() => logger.log(JSON.stringify(data, null, 2))}>% ShareObjectPage %</div>}

      <Paper elevation={3} className="p-6 rounded-lg">
        {/* Переключатель между фото и планировкой */}
        {(photos.length > 0 || layoutPhotos.length > 0) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginY: 2 }}>
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              onChange={handleTabChange}
              aria-label="выбор фото или планировки"
              size="small">
              {photos.length > 0 && (
                <ToggleButton value="photos" size="small">
                  Фото
                </ToggleButton>
              )}
              {layoutPhotos.length > 0 && (
                <ToggleButton value="layout">
                  Планировка
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Галерея изображений */}
        {images.length > 0 && (
          <Box sx={{ marginY: 2 }}>
            {/* Основная галерея */}
            <Swiper
              modules={[Navigation, Pagination, Thumbs]}
              spaceBetween={10}
              slidesPerView={1}
              navigation={{ hideOnClick: true }}
              pagination={{ clickable: true }}
              thumbs={{ swiper: thumbsSwiper }}>
              {images.map((image, index) => (
                <SwiperSlide key={index} onClick={() => handleImageClick(image)}>
                  <img
                    src={image}
                    alt={`Слайд ${index + 1}`}
                    className="w-full rounded-lg cursor-pointer object-cover h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] xl:h-[600px]"
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Миниатюры */}
            {images.length > 1 && (
              <Swiper
                onSwiper={setThumbsSwiper}
                modules={[Thumbs]}
                spaceBetween={10}
                slidesPerView={6}
                watchSlidesProgress>
                {images.map((image, index) => (
                  <SwiperSlide key={`thumb-${index}`}>
                    <img
                      src={image}
                      alt={`Миниатюра ${index + 1}`}
                      className="mt-4 w-full rounded-md cursor-pointer object-cover h-[50px] sm:h-[60px] md:h-[70px] lg:h-[80px] xl:h-[100px]"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Box>
        )}

        {/* Заголовок объекта */}
        <div className="flex mt-6 font-semibold text-3xl">{object.renderTitle}</div>

        {/* Адрес */}
        <div className="font-medium text-xl mt-6">{object.address}</div>
        
        {/* Цена */}
        <div className="font-semibold text-3xl mt-6">
          {object.details.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽
        </div>

        {/* Описание */}
        {object.description && (
          <div className="mt-6">
            <div className="font-semibold text-xl">Описание</div>
            <div className="mt-2">{object.description}</div>
          </div>
        )}

        {/* Динамические поля */}
        <div className="mb-10 mt-8">{renderFields()}</div>

        {/* Полноэкранный просмотр */}
        <Dialog fullScreen open={fullscreenOpen} onClose={() => setFullscreenOpen(false)}>
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <IconButton
              sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}
              onClick={() => setFullscreenOpen(false)}>
              <CloseIcon />
            </IconButton>
            <img
              src={fullscreenImage}
              alt="Полноэкранный просмотр"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default ShareObjectPage;
